import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AvatarUploader from "@/components/AvatarUploader";
import { toast } from "sonner";
import {
  ImageSquare,
  VideoCamera,
  Trash,
  ArrowUpRight,
  UploadSimple,
  WhatsappLogo,
  Phone,
  ChartBar,
} from "@phosphor-icons/react";

const ACCEPT_MEDIA = "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm";

export default function DriverProfileEdit() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [bio, setBio] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");
  const [stats, setStats] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/drivers/me");
      setProfile(data);
      setBio(data.bio || "");
    } catch {}
    try {
      const { data } = await api.get("/drivers/contacts/stats");
      setStats(data);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const saveBio = async () => {
    setBusy(true);
    try {
      await api.put("/drivers/profile", { bio });
      toast.success("Bio updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Max 25 MB");
      e.target.value = "";
      return;
    }
    const name = (file.name || "").toLowerCase();
    if (name.endsWith(".mov") || file.type === "video/quicktime") {
      toast.error(".MOV files don't play in browsers. Please export as MP4 first.");
      e.target.value = "";
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    if (caption.trim()) fd.append("caption", caption.trim());
    try {
      await api.post("/drivers/profile/media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      setCaption("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeMedia = async (id) => {
    if (!confirm("Remove this from your profile?")) return;
    try {
      await api.delete(`/drivers/profile/media/${id}`);
      load();
    } catch { toast.error("Failed"); }
  };

  if (!profile) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar active="profile" />
        <div className="p-8 font-mono text-sm uppercase tracking-wider">Loading…</div>
      </div>
    );
  }

  const media = profile.media || [];

  return (
    <div className="min-h-screen bg-background">
      <Navbar active="profile" />
      <div className="max-w-[1100px] mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-2">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-foreground/60">Profile</div>
          <Link
            data-testid="profile-edit-view-public"
            to={`/d/${user?.user_id}`}
            className="font-mono text-[10px] uppercase tracking-wider underline inline-flex items-center gap-1"
          >
            View public profile <ArrowUpRight size={12} />
          </Link>
        </div>
        <h1 className="font-display text-4xl tracking-tighter mb-8">Make your profile shine</h1>

        {/* Profile photo */}
        <section className="border-2 border-foreground p-6 bg-card mb-6">
          <h2 className="font-display text-2xl mb-4">Profile photo</h2>
          <AvatarUploader size={96} />
        </section>

        {/* Contact analytics */}
        {stats && (
          <section className="border-2 border-foreground p-6 bg-card mb-6" data-testid="profile-contact-stats">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display text-2xl inline-flex items-center gap-2">
                <ChartBar size={22} weight="duotone" /> Inbound contacts
              </h2>
              {stats.last_contact_at && (
                <span className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">
                  Last · {stats.last_contact_at.slice(0, 10)}
                </span>
              )}
            </div>
            <p className="text-foreground/70 text-sm mb-4">
              Riders who tap your WhatsApp or Call button — your inbound demand pipeline.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="border-2 border-foreground p-4 bg-accent text-white">
                <div className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider opacity-80">
                  <WhatsappLogo size={12} weight="fill" /> WhatsApp · total
                </div>
                <div className="font-display text-3xl mt-1" data-testid="stats-whatsapp-total">{stats.whatsapp_clicks}</div>
                <div className="font-mono text-[10px] uppercase tracking-wider opacity-80 mt-1">
                  {stats.whatsapp_7d} in last 7d
                </div>
              </div>
              <div className="border-2 border-foreground p-4 bg-background">
                <div className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-foreground/60">
                  <Phone size={12} weight="fill" /> Calls · total
                </div>
                <div className="font-display text-3xl mt-1" data-testid="stats-call-total">{stats.call_clicks}</div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60 mt-1">
                  {stats.call_7d} in last 7d
                </div>
              </div>
              <div className="border-2 border-foreground p-4 bg-primary">
                <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/70">All-time clicks</div>
                <div className="font-display text-3xl mt-1">{stats.total_contact_clicks}</div>
              </div>
              <div className="border-2 border-foreground p-4 bg-background">
                <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">7-day clicks</div>
                <div className="font-display text-3xl mt-1">{stats.whatsapp_7d + stats.call_7d}</div>
              </div>
            </div>
            {stats.recent && stats.recent.length > 0 && (
              <div className="mt-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-foreground/60 mb-2">Recent · last 20</div>
                <ul className="divide-y divide-foreground/15 border-2 border-foreground/15">
                  {stats.recent.map((c) => (
                    <li key={c.click_id} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="flex items-center gap-2">
                        {c.source === "whatsapp" ? <WhatsappLogo size={14} weight="fill" className="text-accent" /> : <Phone size={14} weight="fill" />}
                        <span className="font-mono uppercase text-[10px] tracking-wider text-foreground/60">{c.source}</span>
                        <span>{c.rider_name || "Anonymous viewer"}</span>
                      </span>
                      <span className="font-mono text-[10px] text-foreground/60">
                        {(c.created_at || "").slice(0, 16).replace("T", " ")}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {/* Bio */}
        <section className="border-2 border-foreground p-6 bg-card mb-6">
          <Label className="font-mono text-xs uppercase tracking-wider">Bio · 400 chars max</Label>
          <Textarea
            data-testid="profile-bio-input"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Hi! I'm Anil — friendly driver, 5+ years on Georgetown roads. Cash only. Safe rides, day or night."
            maxLength={400}
            className="rounded-none mt-2 border-2 border-foreground/30 min-h-[100px]"
          />
          <div className="flex items-center justify-between mt-3">
            <div className="font-mono text-[10px] text-foreground/60">{bio.length}/400</div>
            <Button
              data-testid="profile-bio-save"
              onClick={saveBio}
              disabled={busy}
              className="rounded-none bg-foreground text-background hover:bg-foreground/90"
            >
              Save bio
            </Button>
          </div>
        </section>

        {/* Upload */}
        <section className="border-2 border-foreground p-6 bg-card mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display text-2xl">Gallery</h2>
            <span className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">
              {media.length} {media.length === 1 ? "item" : "items"}
            </span>
          </div>
          <p className="text-foreground/70 text-sm mb-4">
            Show off your car, share short videos of scenic drives, or post a friendly intro clip. Riders see this on your public profile.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
            <Input
              data-testid="profile-media-caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Optional caption…"
              className="rounded-none border-2 border-foreground/30 h-12"
              maxLength={100}
            />
            <label className="cursor-pointer">
              <input
                data-testid="profile-media-file"
                type="file"
                accept={ACCEPT_MEDIA}
                onChange={onUpload}
                className="hidden"
                disabled={uploading}
              />
              <span className={`inline-flex items-center justify-center gap-2 px-4 h-12 border-2 border-foreground font-semibold ${uploading ? "bg-secondary text-foreground/50" : "bg-foreground text-background hover:bg-foreground/90"}`}>
                {uploading ? "Uploading…" : (<>
                  <UploadSimple size={16} /> Upload photo / video
                </>)}
              </span>
            </label>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60 mt-2">
            JPG · PNG · WEBP · MP4 · WEBM · MOV · up to 25 MB
          </div>
        </section>

        {/* Gallery grid */}
        {media.length === 0 ? (
          <div className="border-2 border-dashed border-foreground/30 p-10 text-center text-foreground/60">
            Nothing here yet — upload your first photo or video above.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {media.map((m) => (
              <div key={m.media_id} className="border-2 border-foreground bg-secondary relative group">
                <div className="aspect-square overflow-hidden">
                  {m.kind === "image" ? (
                    <img src={`${API}/files/${m.storage_path}`} alt={m.caption} className="w-full h-full object-cover" />
                  ) : (
                    <video src={`${API}/files/${m.storage_path}`} className="w-full h-full object-cover" controls preload="metadata" />
                  )}
                </div>
                <div className="p-2 flex items-center justify-between bg-card border-t-2 border-foreground">
                  <span className="text-xs truncate flex items-center gap-1">
                    {m.kind === "video" ? <VideoCamera size={12} /> : <ImageSquare size={12} />}
                    {m.caption || (m.kind === "video" ? "Video" : "Photo")}
                  </span>
                  <button
                    data-testid={`profile-media-delete-${m.media_id}`}
                    onClick={() => removeMedia(m.media_id)}
                    title="Remove"
                    className="text-destructive hover:bg-destructive hover:text-white p-1 border border-foreground"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
